import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CatalogueApiService } from '@angular-workspace/services';
import {
  TableComponent,
  TableColumn,
  PaginationConfig,
  withCellTemplates,
} from '../primeng/table/table.component';
import { SingleSelectComponent } from '../primeng/single-select/single-select.component';
import { ModalComponent } from '../primeng/modal/modal.component';
import { ToasterService } from '../../../services/toaster.service';
import { DatasetVersionFileService } from '../../services/dataset-version-file.service';
import {
  DatasetVersionTag,
  extractDatasetVersionMetadata,
  fillMandatoryItemFields,
  validateNewVersionTag,
} from '../../services/dataset-version.model';
import { environment } from '../../../../environments/environment';
import { GROUND_TRUTH_STORAGE_NAME } from '../../utils/tanuh-dataset-item-payload.util';
import {
  buildFileTreeRows,
  collectFolderKeys,
  FileTreeRow,
  readDroppedEntries,
} from '../../utils/file-tree.util';

/** Row in the file list (mapped from the file-server list API). */
export interface DatasetFileRow {
  /** Full S3 key (e.g. "v1.0/data.csv") — used verbatim for delete/download. */
  id: string;
  /** Display-only basename with the version prefix stripped. */
  fileName: string;
  size: string;
  uploadedOn: string;
  status: string;
}

interface UploadedFileItem {
  file: File;
  progress: number;
  status: 'pending' | 'uploading' | 'completed' | 'error';
  error?: string;
  uploadId?: string;
  key?: string;
}

/**
 * TANUH-local "Manage Files" modal: lets a provider create dataset/model versions
 * (free-text tags recorded on the catalogue item) and upload files scoped to a version.
 * Talks only to `DatasetVersionFileService` for file operations — never imports the shared
 * `FileApiService` directly, keeping the versioned-key logic isolated to this app.
 */
@Component({
  selector: 'app-manage-files-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, TableComponent, SingleSelectComponent, ModalComponent],
  templateUrl: './manage-files-modal.component.html',
  styleUrl: './manage-files-modal.component.scss',
})
export class ManageFilesModalComponent implements OnChanges, AfterViewInit {
  @Input({ required: true }) itemId!: string;
  @Input() assetName = '';
  @Output() dismiss = new EventEmitter<void>();

  @ViewChild('actionsCell', { static: true }) actionsCellTemplate!: TemplateRef<any>;

  activeTab: 'files' | 'upload' = 'files';

  // Version metadata (from the catalogue item)
  datasetVersionTags: DatasetVersionTag[] = [];
  datasetCurrentVersion: string | null = null;
  /** Empty string = "Legacy files" (pre-versioning flat uploads). */
  activeVersionTag = '';
  versionMetadataLoaded = false;
  /**
   * Full item exactly as returned by getItemById — PUT /item's documented contract requires
   * "a fully valid item payload including the id", so version updates are built by overlaying
   * currentVersion/versionTags onto this rather than sending a minimal partial body.
   */
  private rawCatalogueItem: Record<string, unknown> | null = null;

  // Create-version UI state
  showCreateVersionInput = false;
  newVersionTagInput = '';
  newVersionTagError: string | null = null;
  creatingVersion = false;

  // Files tab
  viewFilesTableRows: DatasetFileRow[] = [];
  viewFilesListLoading = false;
  viewFilesListError: string | null = null;

  viewFilesTableColumns: TableColumn[] = [
    { key: 'fileName', label: 'File name', sortable: false },
    { key: 'size', label: 'Size', sortable: false },
    { key: 'uploadedOn', label: 'Uploaded on', sortable: false },
    { key: 'actions', label: 'Actions', sortable: false, fixed: 'right', width: '110px', align: 'left' },
  ];

  readonly viewFilesTablePagination: PaginationConfig = {
    enabled: false,
    currentPage: 1,
    pageSize: 100,
    totalItems: 0,
    pageSizeOptions: [],
  };

  // Upload tab — dataset files (multi-file), ported from ProviderUploadComponent's flow.
  dragActive = false;
  selectedFiles: File[] = [];
  uploadedFiles: UploadedFileItem[] = [];
  uploading = false;
  isPublishing = false;

  // Path of each file relative to a chosen/dropped folder (e.g. "images/cats/1.png"); falls back to the file name.
  private filePaths = new WeakMap<File, string>();

  // File tree view state (rebuilt only when the file lists or collapse state change)
  private collapsedFolders = new Set<string>();
  private treeRowsCache: Array<FileTreeRow<File, UploadedFileItem>> = [];
  private treeCacheSelected: File[] = [];
  private treeCacheUploaded: UploadedFileItem[] = [];
  private treeCacheVersion = -1;
  private treeVersion = 0;

  // Ground truth (.json) — mandatory single-file slot, same upload flow.
  groundTruthItem: UploadedFileItem | null = null;

  /** True while the environment enables client-side envelope encryption for dataset uploads. */
  readonly encryptedUploadEnabled = !!environment.encryptedDatasetUpload;

  get viewFilesVersionOptions(): Array<{ label: string; value: string }> {
    const opts = this.datasetVersionTags
      .slice()
      .sort((a, b) => (a.datetime < b.datetime ? 1 : -1))
      .map((v) => ({ label: v.tag, value: v.tag }));
    if (opts.length > 0) {
      opts.push({ label: 'Legacy files (no version)', value: '' });
    }
    return opts;
  }

  constructor(
    private versionFileService: DatasetVersionFileService,
    private catalogueApiService: CatalogueApiService,
    private toasterService: ToasterService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['itemId'] && this.itemId) {
      this.resetState();
      void this.loadVersionMetadata();
    }
  }

  ngAfterViewInit(): void {
    this.viewFilesTableColumns = withCellTemplates(this.viewFilesTableColumns, [
      { key: 'actions', cellTemplate: this.actionsCellTemplate },
    ]);
  }

  private resetState(): void {
    this.activeTab = 'files';
    this.datasetVersionTags = [];
    this.datasetCurrentVersion = null;
    this.activeVersionTag = '';
    this.versionMetadataLoaded = false;
    this.rawCatalogueItem = null;
    this.showCreateVersionInput = false;
    this.newVersionTagInput = '';
    this.newVersionTagError = null;
    this.viewFilesTableRows = [];
    this.viewFilesListError = null;
    this.selectedFiles = [];
    this.uploadedFiles = [];
    this.groundTruthItem = null;
    this.collapsedFolders.clear();
    this.treeVersion++;
  }

  /**
   * GET /iudx/v2/cat/item?id=... (dx-controlplane) returns the item under `results` as a
   * single object — e.g. { type, title, results: { id, name, shortDescription, ... }, detail }
   * (docs/openapi.yaml: successResponseForGetDataBankEntity/AIModelEntity/AdexAppsEntity).
   * Checked first since it's the confirmed real backend shape; the other keys are defensive
   * fallbacks only, in case a differently-versioned backend wraps the item differently.
   */
  private unwrapCatalogueItem(response: any): Record<string, unknown> | null {
    if (!response) return null;
    if (response.results && typeof response.results === 'object' && !Array.isArray(response.results)) {
      return response.results;
    }
    if (Array.isArray(response.results) && response.results.length > 0) {
      return response.results[0] ?? null;
    }
    if (response.result) {
      return Array.isArray(response.result) ? response.result[0] ?? null : response.result;
    }
    if (response.data) {
      return Array.isArray(response.data) ? response.data[0] ?? null : response.data;
    }
    if (Array.isArray(response)) {
      return response[0] ?? null;
    }
    return response;
  }

  private async loadVersionMetadata(): Promise<void> {
    try {
      const response = await this.catalogueApiService.getItemById(this.itemId);
      const item = this.unwrapCatalogueItem(response);
      this.rawCatalogueItem = item;
      const meta = extractDatasetVersionMetadata(item);
      this.datasetVersionTags = meta.versionTags;
      this.datasetCurrentVersion = meta.currentVersion;
      this.activeVersionTag = meta.currentVersion ?? '';
    } catch (e) {
      console.error('loadVersionMetadata:', e);
      this.rawCatalogueItem = null;
      this.datasetVersionTags = [];
      this.datasetCurrentVersion = null;
      this.activeVersionTag = '';
    } finally {
      this.versionMetadataLoaded = true;
      void this.loadFiles();
    }
  }

  setActiveTab(tab: 'files' | 'upload'): void {
    this.activeTab = tab;
    if (tab === 'files') {
      void this.loadFiles();
    }
  }

  onVersionTagSelected(tag: string | null): void {
    this.activeVersionTag = (tag ?? '').trim();
    void this.loadFiles();
  }

  openCreateVersionInput(): void {
    this.showCreateVersionInput = true;
    this.newVersionTagInput = '';
    this.newVersionTagError = null;
  }

  cancelCreateVersionInput(): void {
    this.showCreateVersionInput = false;
    this.newVersionTagInput = '';
    this.newVersionTagError = null;
  }

  async confirmCreateVersion(): Promise<void> {
    const error = validateNewVersionTag(this.newVersionTagInput, this.datasetVersionTags);
    if (error) {
      this.newVersionTagError = error;
      return;
    }
    if (!this.rawCatalogueItem) {
      this.toasterService.error('Asset details are still loading. Please try again in a moment.');
      return;
    }
    const tag = this.newVersionTagInput.trim();
    this.creatingVersion = true;
    this.newVersionTagError = null;
    try {
      const entry: DatasetVersionTag = { tag, datetime: new Date().toISOString() };
      const updatedTags = [...this.datasetVersionTags, entry];
      // PUT /item's documented contract requires "a fully valid item payload including the id" —
      // start from the item exactly as fetched, backfill any blank required field (existing
      // items are sometimes missing shortDescription/description/tags/yearRange), and only
      // overlay the version fields, so every other real field is preserved unchanged.
      await this.catalogueApiService.updateItem({
        ...fillMandatoryItemFields(this.rawCatalogueItem),
        id: this.itemId,
        currentVersion: tag,
        versionTags: updatedTags,
      });
      this.rawCatalogueItem = { ...this.rawCatalogueItem, currentVersion: tag, versionTags: updatedTags };
      this.datasetVersionTags = updatedTags;
      this.datasetCurrentVersion = tag;
      this.activeVersionTag = tag;
      this.showCreateVersionInput = false;
      this.newVersionTagInput = '';
      this.selectedFiles = [];
      this.uploadedFiles = [];
        this.groundTruthItem = null;
      this.toasterService.success(`Version "${tag}" created.`);
      this.setActiveTab('upload');
    } catch (e: any) {
      console.error('confirmCreateVersion:', e);
      this.toasterService.error('Failed to create version. Please try again.');
    } finally {
      this.creatingVersion = false;
    }
  }

  private extractFilesFromListResponse(body: unknown): Record<string, unknown>[] {
    if (!body || typeof body !== 'object') return [];
    const o = body as Record<string, unknown>;
    if (Array.isArray(o['files'])) return o['files'] as Record<string, unknown>[];
    const nested = o['data'];
    if (nested && typeof nested === 'object' && Array.isArray((nested as any)['files'])) {
      return (nested as any)['files'] as Record<string, unknown>[];
    }
    return [];
  }

  private formatFileSizeBytes(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.min(sizes.length - 1, Math.floor(Math.log(bytes) / Math.log(k)));
    return `${Math.round((bytes / Math.pow(k, i)) * 100) / 100} ${sizes[i]}`;
  }

  private isUnderVersionFolder(key: string): boolean {
    return this.datasetVersionTags.some((v) => key.startsWith(`${v.tag}/`));
  }

  private mapApiFileToRow(item: Record<string, unknown>, index: number, versionTag = ''): DatasetFileRow {
    const key = String(item['key'] ?? item['name'] ?? '').trim() || `file-${index + 1}`;
    // Show the path inside the version (keeps folder structure), without the version prefix
    const displayName = versionTag && key.startsWith(`${versionTag}/`)
      ? key.slice(versionTag.length + 1)
      : key;
    const sizeRaw = item['size'];
    const bytes =
      typeof sizeRaw === 'number' && !isNaN(sizeRaw) ? sizeRaw : parseInt(String(sizeRaw ?? ''), 10);
    const lm = item['lastModified'] ?? item['last_modified'];
    let uploadedOn = '—';
    if (lm != null && String(lm).trim()) {
      const d = new Date(String(lm));
      if (!isNaN(d.getTime())) {
        uploadedOn = d.toISOString().split('T')[0];
      }
    }
    const status = String(item['status'] ?? 'Ready').trim() || 'Ready';
    return {
      id: key,
      fileName: displayName || key,
      size: !isNaN(bytes) && bytes > 0 ? this.formatFileSizeBytes(bytes) : '—',
      uploadedOn,
      status,
    };
  }

  private async loadFiles(): Promise<void> {
    if (this.viewFilesListLoading) return;
    this.viewFilesListLoading = true;
    this.viewFilesListError = null;
    try {
      const tag = this.activeVersionTag.trim();
      const res = await this.versionFileService.listVersionFiles(this.itemId, tag || null);
      const raw = this.extractFilesFromListResponse(res?.data);
      this.viewFilesTableRows = raw
        .filter((f) => f['isFile'] !== false)
        // Legacy view: everything except files that live under a version folder
        .filter((f) => (tag ? true : !this.isUnderVersionFolder(String(f['key'] ?? ''))))
        .map((f, i) => this.mapApiFileToRow(f, i, tag));
    } catch (e: any) {
      console.error('loadFiles:', e);
      this.viewFilesListError = e?.message ?? 'Failed to load files.';
      this.viewFilesTableRows = [];
    } finally {
      this.viewFilesListLoading = false;
    }
  }

  async downloadFile(row: DatasetFileRow): Promise<void> {
    try {
      const { presignedUrl } = await this.versionFileService.getPresignedDownloadUrl(
        this.itemId,
        row.id,
      );
      const link = document.createElement('a');
      link.href = presignedUrl;
      link.download = row.fileName;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (e) {
      console.error('downloadFile:', e);
      this.toasterService.error('Failed to download file.');
    }
  }

  async deleteFile(row: DatasetFileRow): Promise<void> {
    try {
      await this.versionFileService.deleteFile(this.itemId, row.id);
      this.toasterService.success(`"${row.fileName}" was deleted.`);
      await this.loadFiles();
    } catch (e) {
      console.error('deleteFile:', e);
      this.toasterService.error('Could not delete the file.');
    }
  }

  // ---- Upload tab (drag/drop + 5-step multipart, versioned) ----

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragActive = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragActive = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragActive = false;
    const items = event.dataTransfer?.items;
    const entries = items
      ? Array.from(items).map((i) => (i as any).webkitGetAsEntry?.() as FileSystemEntry | null)
      : [];
    if (entries.length > 0 && entries.every((e) => !!e)) {
      // Entries must be grabbed synchronously: DataTransferItems are invalidated after the handler returns
      readDroppedEntries(entries as FileSystemEntry[]).then((dropped) => {
        dropped.forEach((d) => this.filePaths.set(d.file, d.path));
        this.addFiles(dropped.map((d) => d.file));
      });
    } else if (event.dataTransfer?.files) {
      this.addFiles(Array.from(event.dataTransfer.files));
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      this.addFiles(Array.from(input.files));
      input.value = '';
    }
  }

  /**
   * Path used as the object name (below the version folder): the folder-relative path for files
   * from a chosen/dropped folder, or just the file name.
   */
  getFilePath(file: File): string {
    return this.filePaths.get(file) || file.webkitRelativePath || file.name;
  }

  private addFiles(files: File[]): void {
    for (const file of files) {
      const path = this.getFilePath(file);
      const exists =
        this.selectedFiles.some((f) => this.getFilePath(f) === path && f.size === file.size) ||
        this.uploadedFiles.some((f) => this.getFilePath(f.file) === path && f.file.size === file.size);
      if (!exists) {
        this.selectedFiles.push(file);
      }
    }
  }

  removeSelectedFile(file: File): void {
    this.selectedFiles = this.selectedFiles.filter((f) => f !== file);
  }

  clearSelectedFiles(): void {
    this.selectedFiles = [];
  }

  // ---- Overall upload progress ----

  getTotalFileCount(): number {
    return this.uploadedFiles.length + this.selectedFiles.length;
  }

  getCompletedFileCount(): number {
    return this.uploadedFiles.filter((f) => f.status === 'completed').length;
  }

  getFailedFileCount(): number {
    return this.uploadedFiles.filter((f) => f.status === 'error').length;
  }

  /** Overall percentage, counting in-flight files by their own progress. */
  getOverallProgressPercent(): number {
    const total = this.getTotalFileCount();
    if (total === 0) return 0;
    const done = this.uploadedFiles.reduce(
      (sum, f) => sum + (f.status === 'completed' ? 100 : f.status === 'uploading' ? f.progress : 0),
      0,
    );
    return Math.round(done / total);
  }

  // ---- File tree ----

  trackTreeRow(_index: number, row: FileTreeRow<File, UploadedFileItem>): string {
    return row.kind + ':' + row.key;
  }

  hasFolders(): boolean {
    return this.getTreeRows().some((r) => r.kind === 'folder');
  }

  toggleFolder(key: string): void {
    if (!this.collapsedFolders.delete(key)) {
      this.collapsedFolders.add(key);
    }
    this.treeVersion++;
  }

  collapseAllFolders(): void {
    collectFolderKeys([
      ...this.selectedFiles.map((f) => this.getFilePath(f)),
      ...this.uploadedFiles.map((f) => this.getFilePath(f.file)),
    ]).forEach((k) => this.collapsedFolders.add(k));
    this.treeVersion++;
  }

  expandAllFolders(): void {
    this.collapsedFolders.clear();
    this.treeVersion++;
  }

  /** Flattened, currently-visible tree rows. Rebuilt only when the file lists or collapse state change. */
  getTreeRows(): Array<FileTreeRow<File, UploadedFileItem>> {
    const sameSel =
      this.treeCacheSelected.length === this.selectedFiles.length &&
      this.selectedFiles.every((f, i) => f === this.treeCacheSelected[i]);
    const sameUp =
      this.treeCacheUploaded.length === this.uploadedFiles.length &&
      this.uploadedFiles.every((f, i) => f === this.treeCacheUploaded[i]);
    if (sameSel && sameUp && this.treeCacheVersion === this.treeVersion) {
      return this.treeRowsCache;
    }
    this.treeRowsCache = buildFileTreeRows<File, UploadedFileItem>(
      this.selectedFiles.map((file) => ({ path: this.getFilePath(file), ref: file })),
      this.uploadedFiles.map((item) => ({ path: this.getFilePath(item.file), ref: item })),
      this.collapsedFolders,
    );
    this.treeCacheSelected = [...this.selectedFiles];
    this.treeCacheUploaded = [...this.uploadedFiles];
    this.treeCacheVersion = this.treeVersion;
    return this.treeRowsCache;
  }

  isAllCompleted(): boolean {
    return this.uploadedFiles.length > 0 && this.uploadedFiles.every((f) => f.status === 'completed');
  }

  async uploadAllFiles(): Promise<void> {
    if (this.uploading || this.selectedFiles.length === 0) return;

    for (const file of this.selectedFiles) {
      const exists = this.uploadedFiles.some((f) => this.getFilePath(f.file) === this.getFilePath(file) && f.file.size === file.size);
      if (!exists) {
        this.uploadedFiles.push({ file, progress: 0, status: 'pending' });
      }
    }
    this.selectedFiles = [];

    this.uploading = true;
    const pending = this.uploadedFiles.filter((f) => f.status === 'pending' || f.status === 'error');
    for (const fileItem of pending) {
      await this.uploadSingleFile(fileItem);
    }
    this.uploading = false;

    if (this.isAllCompleted()) {
      this.toasterService.success('All files uploaded successfully!');
    }
  }

  /**
   * Uploads one file, scoped to the active version. When `environment.encryptedDatasetUpload`
   * is on, delegates to the same client-side AES-256-GCM chunked + KMS-wrapped-DEK flow used by
   * the onboarding "Upload files" step (`EncryptedUploadService`, via the wrapper); otherwise
   * uses the plain 5-step multipart flow. Shared by dataset files and ground truth — both go through the same encryption path, matching the onboarding flow.
   *
   * `storageFileName` overrides the file's own name as the S3 object name — used to give
   * ground truth a standardized name regardless of what the user's local
   * file is called, so downstream consumers can find them without listing. Dataset files omit
   * it and keep their own filename.
   */
  private async uploadSingleFile(fileItem: UploadedFileItem, storageFileName?: string): Promise<void> {
    try {
      fileItem.status = 'uploading';
      fileItem.progress = 0;
      fileItem.error = undefined;

      const versionTag = this.activeVersionTag.trim() || null;
      const file = fileItem.file;
      const fileName = storageFileName ?? this.getFilePath(file);

      if (this.encryptedUploadEnabled) {
        await this.versionFileService.uploadEncryptedFile(
          this.itemId,
          versionTag,
          fileName,
          file,
          (pct) => { fileItem.progress = Math.round(pct * 0.85); },
        );
        fileItem.progress = 90;
        await this.versionFileService.listVersionFiles(this.itemId, versionTag);
        fileItem.progress = 100;
        fileItem.status = 'completed';
        fileItem.key = this.versionFileService.buildVersionedKey(versionTag, fileName);
        return;
      }

      const initiateResponse = await this.versionFileService.initiateUpload(
        this.itemId,
        versionTag,
        fileName,
        file.type || 'application/octet-stream',
      );
      const responseData = initiateResponse.data?.data ?? initiateResponse.data;
      const uploadId = responseData?.uploadId;
      const presignedUrls = responseData?.parts?.map((p: any) => p.presignedUrl) ?? [];
      if (!uploadId || presignedUrls.length === 0) {
        throw new Error('No presigned URLs received from server');
      }
      fileItem.progress = 10;

      const uploadResponse = await this.versionFileService.uploadPart(presignedUrls[0], file);
      const etag = uploadResponse.headers.get('ETag')?.replace(/"/g, '') ?? '';
      if (!etag) {
        throw new Error('No ETag received from storage upload');
      }
      fileItem.progress = 40;

      await this.versionFileService.completeUpload(this.itemId, uploadId, versionTag, fileName, [
        { partNumber: 1, eTag: etag },
      ]);
      fileItem.progress = 60;

      await this.versionFileService.generatePreview(this.itemId, versionTag, fileName);
      fileItem.progress = 100;
      fileItem.status = 'completed';
      fileItem.uploadId = uploadId;
      fileItem.key = this.versionFileService.buildVersionedKey(versionTag, fileName);
    } catch (error: any) {
      fileItem.status = 'error';
      fileItem.error = error?.message ?? 'Upload failed. Please try again.';
      console.error('Upload error:', error);
    }
  }

  private getFileExtension(name: string): string {
    const i = name.lastIndexOf('.');
    return i >= 0 ? '.' + name.slice(i + 1).toLowerCase() : '';
  }

  private async removeFileFromServer(fileItem: UploadedFileItem): Promise<void> {
    if (fileItem.status === 'completed' && fileItem.key) {
      await this.versionFileService.deleteFile(this.itemId, fileItem.key);
    } else if (fileItem.uploadId && fileItem.key) {
      await this.versionFileService.cancelUpload(this.itemId, fileItem.uploadId, fileItem.key);
    }
  }

  // ---- Ground truth (.json) — single-file slot ----

  onGroundTruthSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (file) {
      if (this.getFileExtension(file.name) !== '.json') {
        this.toasterService.error('Only .json files can be uploaded for Ground Truth.');
      } else {
        this.groundTruthItem = { file, progress: 0, status: 'pending' };
      }
    }
    input.value = '';
  }

  async uploadGroundTruthFile(): Promise<void> {
    if (!this.groundTruthItem || this.groundTruthItem.status === 'uploading') {
      return;
    }
    await this.uploadSingleFile(this.groundTruthItem, GROUND_TRUTH_STORAGE_NAME);
    if (this.groundTruthItem.status === 'completed') {
      this.toasterService.success('Ground truth file uploaded successfully!');
    }
  }

  async deleteGroundTruthFile(): Promise<void> {
    if (!this.groundTruthItem) return;
    try {
      await this.removeFileFromServer(this.groundTruthItem);
      this.groundTruthItem = null;
      this.toasterService.success('Ground truth file removed successfully');
    } catch (e: any) {
      console.error('deleteGroundTruthFile:', e);
      this.toasterService.error('Failed to remove file: ' + (e?.message ?? 'Unknown error'));
    }
  }

  getFileSize(bytes: number): string {
    return this.formatFileSizeBytes(bytes);
  }

  async deleteUploadedFile(fileItem: UploadedFileItem): Promise<void> {
    try {
      await this.removeFileFromServer(fileItem);
      this.uploadedFiles = this.uploadedFiles.filter((f) => f !== fileItem);
    } catch (e: any) {
      console.error('deleteUploadedFile:', e);
      this.toasterService.error('Failed to remove file: ' + (e?.message ?? 'Unknown error'));
    }
  }

  /** Publish needs every dataset file uploaded and the (mandatory) ground truth uploaded. */
  canPublish(): boolean {
    return this.isAllCompleted() && this.groundTruthItem?.status === 'completed';
  }

  async publish(): Promise<void> {
    if (!this.isAllCompleted()) {
      this.toasterService.error('Upload all selected files before publishing.');
      return;
    }
    if (this.groundTruthItem?.status !== 'completed') {
      this.toasterService.error('Please upload the ground truth file before publishing.');
      return;
    }
    try {
      this.isPublishing = true;
      const response = await this.versionFileService.publishDatabank(this.itemId);
      if (response.status === 200 || response.status === 202) {
        this.toasterService.success('Files published successfully');
        this.uploadedFiles = [];
        this.groundTruthItem = null;
        this.setActiveTab('files');
      } else {
        throw new Error(`Unexpected response status: ${response.status}`);
      }
    } catch (e: any) {
      console.error('publish:', e);
      this.toasterService.error('Failed to publish: ' + (e?.message ?? 'Unknown error'));
    } finally {
      this.isPublishing = false;
    }
  }

  close(): void {
    this.dismiss.emit();
  }
}
